// Paths picked by clicking the response tree. A node is addressed by its segments from the body:
// object keys as strings, list indices as numbers. Same syntax the server reads (S9).
export const MAX_LIST_LEVELS = 2;

const isIndex = (segment) => typeof segment === 'number';
const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const join = (segments) => segments.join('.');
const parts = (path) => (path ? path.split('.') : []);
const startsWith = (segments, prefix) => prefix.every((part, i) => String(segments[i]) === part);

const readAt = (body, segments) =>
  segments.reduce((value, segment) => (value === null || value === undefined ? undefined : value[segment]), body);

// "rooms[].variants[]" → ['rooms', 'variants']; null for a root without [].
export const listLevels = (root) =>
  root.includes('[]')
    ? root
        .split('[]')
        .slice(0, -1)
        .map((part) => part.replace(/^\./, ''))
    : null;

// The root a click on a list or an object writes. A list under another list's items becomes a []
// root, one [] per level; null where the server cannot read it (a list of lists, more than two levels).
export const rootFor = (segments, value, { dictionary = false } = {}) => {
  const levels = [[]];
  segments.forEach((segment) => (isIndex(segment) ? levels.push([]) : levels[levels.length - 1].push(segment)));

  if (levels.length === 1) {
    if (Array.isArray(value)) return { root: join(segments), rootKind: 'array' };
    if (isObject(value)) return { root: join(segments), rootKind: dictionary ? 'dictionary' : 'object' };
    return null;
  }
  if (!Array.isArray(value) || levels.length > MAX_LIST_LEVELS || levels.some((level) => !level.length)) return null;
  return { root: `${levels.map(join).join('[].')}[]`, rootKind: 'array' };
};

// The mapping path of a clicked node, relative to its row: '' for the row itself, ^.<path> for the
// row one level up under a [] root, $key for a dictionary entry; null outside the rows.
export const fieldFor = ({ root = '', rootKind = 'array' }, segments, body) => {
  const levels = listLevels(root.trim());
  if (levels) {
    let rest = segments;
    for (let i = 0; i < levels.length; i += 1) {
      const level = parts(levels[i]);
      if (!startsWith(rest, level) || !isIndex(rest[level.length])) {
        // Inside the row one level up but not in the inner list: what ^. reads.
        const last = i > 0 && i === levels.length - 1;
        return last && rest.length && !startsWith(rest, level) ? `^.${join(rest)}` : null;
      }
      rest = rest.slice(level.length + 1);
    }
    return join(rest);
  }

  const base = parts(root.trim());
  if (!startsWith(segments, base)) return null;
  let rest = segments.slice(base.length);
  if (rootKind === 'dictionary') {
    if (!rest.length) return null;
    return rest.length === 1 ? '$key' : join(rest.slice(1));
  }
  if (Array.isArray(readAt(body, base))) {
    if (!isIndex(rest[0])) return null;
    rest = rest.slice(1);
  }
  return join(rest);
};

// A row field name for a picked path: its last key, made unique among the names already taken.
export const fieldNameFor = (path, taken) => {
  const base =
    path === '$key'
      ? 'key'
      : path
          .replace(/^\^\./, '')
          .split('.')
          .filter((part) => !/^\d+$/.test(part))
          .pop() || 'field';
  let name = base;
  for (let n = 2; taken.includes(name); n += 1) name = `${base}${n}`;
  return name;
};
